import React, { useState, useEffect } from 'react';
import {
    Modal,
    Form,
    Input,
    Select,
    Button,
    message,
    Typography,
    Space,
    Card,
    Tag,
    Descriptions,
    Divider,
    Tooltip,
    Alert
} from 'antd';
import {
    FileTextOutlined,
    ExclamationCircleOutlined,
    ShoppingOutlined,
    DollarOutlined,
    NumberOutlined,
    CheckCircleOutlined,
    TagOutlined,
    QuestionCircleOutlined,
    RocketOutlined,
    InfoCircleOutlined,
    CalendarOutlined,
    CloseOutlined,
    ClockCircleOutlined,
    MailOutlined,
    EyeOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import disputeService from '../../services/disputeService';
import auctionService from '../../services/auctionService';
import api from '../../lib/api';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const FileDisputeModal = ({ visible, onCancel, onSuccess, lotId = null }) => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [auctions, setAuctions] = useState([]); // Always initialize as array
    const [lots, setLots] = useState([]); // Always initialize as array
    const [selectedAuction, setSelectedAuction] = useState(null);
    const [selectedLot, setSelectedLot] = useState(null);
    const [loadingAuctions, setLoadingAuctions] = useState(false);
    const [loadingLots, setLoadingLots] = useState(false);
    const [successModalVisible, setSuccessModalVisible] = useState(false);
    const [filedDispute, setFiledDispute] = useState(null);

    useEffect(() => {
        if (visible && !lotId) {
            loadUserAuctions();
            form.resetFields();
            setSelectedAuction(null);
            setSelectedLot(null);
            setLots([]);
            setSuccessModalVisible(false);
            setFiledDispute(null);
        } else if (visible && lotId) {
            loadLotDetails(lotId);
            setSuccessModalVisible(false);
            setFiledDispute(null);
        }
    }, [visible, lotId]);

    const loadUserAuctions = async () => {
        try {
            setLoadingAuctions(true);
            const response = await disputeService.getDisputableAuctions();
            if (response.status === 'success') {
                // Ensure data is an array
                const auctionsData = Array.isArray(response.data) ? response.data : [];
                setAuctions(auctionsData);
                
                if (auctionsData.length === 0) {
                    message.info('No auctions available for dispute filing. You need completed auctions where you are either the seller (with sold lots) or the buyer (with winning bids).');
                }
            } else {
                setAuctions([]);
            }
        } catch (error) {
            console.error('Error loading auctions:', error);
            message.error('Failed to load auctions');
            setAuctions([]);
        } finally {
            setLoadingAuctions(false);
        }
    };

    const loadLotsForAuction = async (auctionSlug) => {
        try {
            setLoadingLots(true);
            setLots([]);
            setSelectedLot(null);
            form.setFieldsValue({ lot_id: undefined });
            
            // Use the disputable lots endpoint which filters by user
            const response = await disputeService.getDisputableLots(auctionSlug);
            if (response.status === 'success') {
                const disputableLots = Array.isArray(response.data) ? response.data : [];
                setLots(disputableLots);
                
                if (disputableLots.length === 0) {
                    message.info('No disputable lots found in this auction. You can only dispute lots you purchased (won) or sold.');
                }
            }
        } catch (error) {
            console.error('Error loading lots:', error);
            message.error('Failed to load lots for this auction');
            setLots([]);
        } finally {
            setLoadingLots(false);
        }
    };

    const loadLotDetails = async (id) => {
        try {
            setLoadingLots(true);
            // You'll need to implement a method to get lot by ID
            // For now, we'll just set the lotId in the form
            form.setFieldsValue({ lot_id: id });
        } catch (error) {
            message.error('Failed to load lot details');
        } finally {
            setLoadingLots(false);
        }
    };

    const handleAuctionChange = (auctionSlug) => {
        const auction = auctions.find(a => a.slug === auctionSlug);
        setSelectedAuction(auction);
        if (auctionSlug) {
            loadLotsForAuction(auctionSlug);
        } else {
            setLots([]);
            setSelectedLot(null);
            form.setFieldsValue({ lot_id: undefined });
        }
    };

    const handleLotChange = (value) => {
        const lot = lots.find(l => l.id === value);
        setSelectedLot(lot);
    };

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            // Only send lot_id to the backend (auction_slug is just for UI)
            const disputeData = {
                lot_id: values.lot_id,
                type: values.type,
                subject: values.subject,
                description: values.description,
                priority: values.priority,
                evidence: values.evidence || []
            };
            const response = await disputeService.createDispute(disputeData);
            if (response.status === 'success') {
                // Store the dispute data and show success modal
                setFiledDispute(response.data);
                form.resetFields();
                setSelectedLot(null);
                setSelectedAuction(null);
                setLots([]);
                // Close the form modal and show success modal
                setSuccessModalVisible(true);
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to file dispute';
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const disputeTypes = [
        { value: 'item_not_received', label: 'Item Not Received' },
        { value: 'item_not_as_described', label: 'Item Not As Described' },
        { value: 'damaged_item', label: 'Damaged Item' },
        { value: 'wrong_item', label: 'Wrong Item' },
        { value: 'payment_issue', label: 'Payment Issue' },
        { value: 'refund_request', label: 'Refund Request' },
        { value: 'other', label: 'Other' }
    ];

    return (
        <>
        <Modal
            title={null}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            centered
            width={900}
            destroyOnClose
            closable={true}
            maskClosable={true}
            className="file-dispute-modal"
            style={{ padding: 0 }}
        >
            <div style={{ 
                background: 'linear-gradient(135deg, #fff5f5 0%, #ffffff 100%)',
                borderRadius: '12px',
                overflow: 'hidden'
            }}>
                {/* Header Section */}
                <div style={{
                    background: 'linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%)',
                    padding: '20px 24px 16px 24px',
                    textAlign: 'center',
                    borderBottom: 'none'
                }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        margin: '0 auto 12px',
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}>
                        <ExclamationCircleOutlined style={{ fontSize: '24px', color: 'white' }} />
                    </div>
                    <Title level={4} style={{ 
                        color: 'white', 
                        margin: 0,
                        fontWeight: 600,
                        fontSize: '20px'
                    }}>
                        File a Dispute
                    </Title>
                    <Text style={{ 
                        color: 'rgba(255, 255, 255, 0.9)', 
                        fontSize: '13px',
                        display: 'block',
                        marginTop: '6px'
                    }}>
                        Fill in the details below to file a dispute. Please provide accurate information to help us resolve your issue quickly.
                    </Text>
                </div>

                {/* Content Section */}
                <div style={{ padding: '24px' }}>
                    <Alert
                        message={<Text strong style={{ fontSize: '13px' }}>Important Information</Text>}
                        description={
                            <Text style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                                Our support team will review your dispute and respond within 24-48 hours. Make sure all information is accurate to help us resolve your issue quickly.
                            </Text>
                        }
                        type="info"
                        showIcon
                        icon={<InfoCircleOutlined />}
                        style={{ 
                            borderRadius: '8px',
                            border: '1px solid #91d5ff',
                            marginBottom: '24px'
                        }}
                    />

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                    priority: 'medium'
                }}
            >
                <Form.Item
                    name="auction_slug"
                    label={
                        <Space size="small">
                            <span style={{ fontWeight: 500, fontSize: '15px' }}>Select Auction</span>
                            <Tooltip title="Choose the auction that contains the lot you want to dispute">
                                <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                            </Tooltip>
                        </Space>
                    }
                    rules={[{ required: true, message: 'Please select an auction' }]}
                    extra={
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                            🎯 Select the auction first, then choose the specific lot
                        </Text>
                    }
                >
                    <Select
                        placeholder="Search and select an auction..."
                        size="large"
                        loading={loadingAuctions}
                        disabled={!!lotId}
                        onChange={handleAuctionChange}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        style={{ borderRadius: '8px' }}
                    >
                        {!Array.isArray(auctions) || auctions.length === 0 ? (
                            <Option disabled value="">
                                {loadingAuctions ? 'Loading auctions...' : 'No disputable auctions available'}
                            </Option>
                        ) : (
                            auctions.map(auction => (
                                <Option key={auction.slug || auction.id} value={auction.slug}>
                                    {auction.title}
                                </Option>
                            ))
                        )}
                    </Select>
                    {(!Array.isArray(auctions) || auctions.length === 0) && !loadingAuctions && (
                        <Alert
                            message="No Disputable Auctions"
                            description="You need completed auctions where you are either the seller (with sold lots) or the buyer (with winning bids) to file a dispute."
                            type="info"
                            showIcon
                            style={{ marginTop: 8, borderRadius: '8px' }}
                        />
                    )}
                </Form.Item>

                <Form.Item
                    name="lot_id"
                    label={
                        <Space size="small">
                            <span style={{ fontWeight: 500, fontSize: '15px' }}>Select Lot</span>
                            <Tooltip title="Choose the specific lot from the selected auction that you want to dispute">
                                <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                            </Tooltip>
                        </Space>
                    }
                    rules={[{ required: true, message: 'Please select a lot' }]}
                    extra={
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                            📦 Select the specific lot you want to dispute from the auction above
                        </Text>
                    }
                >
                    <Select
                        placeholder={selectedAuction ? "Search and select a lot..." : "Please select an auction first"}
                        size="large"
                        loading={loadingLots}
                        disabled={!!lotId || !selectedAuction || lots.length === 0}
                        onChange={handleLotChange}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        notFoundContent={selectedAuction && lots.length === 0 ? "No disputable lots in this auction" : "Select an auction first"}
                        style={{ borderRadius: '8px' }}
                    >
                        {lots.map(lot => (
                            <Option key={lot.id} value={lot.id}>
                                Lot #{lot.lot_number}: {lot.title} - PKR {lot.current_price}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                {selectedLot && (
                    <Card 
                        size="small" 
                        style={{ 
                            marginBottom: 20,
                            background: 'linear-gradient(135deg, #f6f9fc 0%, #ffffff 100%)',
                            border: '1px solid #e8f4f8',
                            borderRadius: 8,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                        }}
                        bodyStyle={{ padding: '16px 20px' }}
                    >
                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 8,
                                marginBottom: 4
                            }}>
                                <TagOutlined style={{ color: '#1890ff', fontSize: 16 }} />
                                <Text strong style={{ fontSize: 15, color: '#262626' }}>
                                    Selected Lot Details
                                </Text>
                            </div>
                            <Divider style={{ margin: '8px 0' }} />
                            <Descriptions 
                                column={1} 
                                size="small"
                                labelStyle={{ 
                                    fontWeight: 600, 
                                    color: '#595959',
                                    width: '35%',
                                    fontSize: '13px'
                                }}
                                contentStyle={{ 
                                    color: '#262626',
                                    fontSize: '13px'
                                }}
                            >
                                <Descriptions.Item 
                                    label={
                                        <Space size={4}>
                                            <NumberOutlined style={{ color: '#1890ff' }} />
                                            <span>Lot Number</span>
                                        </Space>
                                    }
                                >
                                    <Tag color="blue" style={{ borderRadius: 4, fontWeight: 600 }}>
                                        #{selectedLot.lot_number}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item 
                                    label={
                                        <Space size={4}>
                                            <ShoppingOutlined style={{ color: '#1890ff' }} />
                                            <span>Title</span>
                                        </Space>
                                    }
                                >
                                    <Text strong>{selectedLot.title}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item 
                                    label={
                                        <Space size={4}>
                                            <FileTextOutlined style={{ color: '#1890ff' }} />
                                            <span>Auction</span>
                                        </Space>
                                    }
                                >
                                    {selectedAuction?.title || 'N/A'}
                                </Descriptions.Item>
                                <Descriptions.Item 
                                    label={
                                        <Space size={4}>
                                            <DollarOutlined style={{ color: '#52c41a' }} />
                                            <span>Final Price</span>
                                        </Space>
                                    }
                                >
                                    <Text strong style={{ color: '#52c41a', fontSize: '14px' }}>
                                        PKR {selectedLot.current_price?.toLocaleString() || selectedLot.current_price}
                                    </Text>
                                </Descriptions.Item>
                            </Descriptions>
                        </Space>
                    </Card>
                )}

                <Divider style={{ margin: '24px 0', borderColor: '#e8f4f8' }} />

                <Form.Item
                    name="type"
                    label={
                        <Space size="small">
                            <span style={{ fontWeight: 500, fontSize: '15px' }}>Dispute Type</span>
                            <Tooltip title="Select the category that best describes your dispute">
                                <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                            </Tooltip>
                        </Space>
                    }
                    rules={[{ required: true, message: 'Please select a dispute type' }]}
                    extra={
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                            🏷️ Choose the category that best matches your issue
                        </Text>
                    }
                >
                    <Select 
                        size="large" 
                        placeholder="Select dispute type..."
                        style={{ borderRadius: '8px' }}
                    >
                        {disputeTypes.map(type => (
                            <Option key={type.value} value={type.value}>
                                {type.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="subject"
                    label={
                        <Space size="small">
                            <span style={{ fontWeight: 500, fontSize: '15px' }}>Subject</span>
                            <Tooltip title="A brief summary of your dispute issue">
                                <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                            </Tooltip>
                        </Space>
                    }
                    rules={[
                        { required: true, message: 'Please enter a subject' },
                        { max: 255, message: 'Subject must be less than 255 characters' }
                    ]}
                    extra={
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                            📝 A brief, clear summary of your dispute (maximum 255 characters)
                        </Text>
                    }
                >
                    <Input
                        size="large"
                        placeholder="e.g., Item not received after payment"
                        maxLength={255}
                        showCount
                        style={{ borderRadius: '8px' }}
                    />
                </Form.Item>

                <Form.Item
                    name="description"
                    label={
                        <Space size="small">
                            <span style={{ fontWeight: 500, fontSize: '15px' }}>Detailed Description</span>
                            <Tooltip title="Provide a comprehensive description of your dispute with all relevant details">
                                <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                            </Tooltip>
                        </Space>
                    }
                    rules={[
                        { required: true, message: 'Please provide a detailed description' },
                        { min: 20, message: 'Description must be at least 20 characters' }
                    ]}
                    extra={
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                            ✍️ Provide a detailed description including dates, circumstances, and any relevant information
                        </Text>
                    }
                >
                    <TextArea
                        rows={6}
                        size="large"
                        placeholder="Provide a detailed description of the dispute. Include any relevant information, dates, and circumstances. The more details you provide, the better we can help resolve your issue."
                        showCount
                        maxLength={2000}
                        style={{ borderRadius: '8px' }}
                    />
                </Form.Item>

                <Form.Item
                    name="priority"
                    label={
                        <Space size="small">
                            <span style={{ fontWeight: 500, fontSize: '15px' }}>Priority Level</span>
                            <Tooltip title="Set the urgency level of your dispute. Urgent issues are reviewed first">
                                <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                            </Tooltip>
                        </Space>
                    }
                    extra={
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                            ⚡ Set the urgency level (default: Medium)
                        </Text>
                    }
                >
                    <Select size="large" style={{ borderRadius: '8px' }}>
                        <Option value="low">
                            <Space>
                                <Tag color="default">Low</Tag>
                                <span>Standard priority</span>
                            </Space>
                        </Option>
                        <Option value="medium">
                            <Space>
                                <Tag color="blue">Medium</Tag>
                                <span>Normal priority (default)</span>
                            </Space>
                        </Option>
                        <Option value="high">
                            <Space>
                                <Tag color="orange">High</Tag>
                                <span>Important issue</span>
                            </Space>
                        </Option>
                        <Option value="urgent">
                            <Space>
                                <Tag color="red">Urgent</Tag>
                                <span>Requires immediate attention</span>
                            </Space>
                        </Option>
                    </Select>
                </Form.Item>

                <Divider style={{ margin: '32px 0', borderColor: '#e8f4f8' }} />

                <Form.Item style={{ marginBottom: 0 }}>
                    <div style={{ 
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 12
                    }}>
                        <Button 
                            onClick={onCancel} 
                            size="large"
                            icon={<CloseOutlined />}
                            style={{ 
                                minWidth: 100,
                                borderRadius: '8px',
                                height: '44px'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            icon={<FileTextOutlined />}
                            size="large"
                            style={{
                                minWidth: 160,
                                borderRadius: '8px',
                                height: '44px',
                                fontWeight: 500,
                                boxShadow: '0 2px 8px rgba(255, 77, 79, 0.3)',
                                background: 'linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%)',
                                border: 'none'
                            }}
                        >
                            {loading ? 'Filing...' : 'File Dispute'}
                        </Button>
                    </div>
                </Form.Item>
            </Form>
                </div>
            </div>
            <style>{`
                .file-dispute-modal .ant-modal-content {
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
                }
                
                .file-dispute-modal .ant-modal-close {
                    display: flex;
                    font-size: 16px;
                    font-style: normal;
                    line-height: 32px;
                    justify-content: center;
                    text-transform: none;
                    text-rendering: auto;
                    color: #ffffff;
                    padding: 10px;
                    background: #333333;
                    border-radius: 50%;
                    width: 30px;
                    height: 30px;
                    top: 12px;
                    right: 12px;
                }
                
                .file-dispute-modal .ant-modal-close:hover {
                    color: white;
                }
            `}</style>
        </Modal>

        {/* Success Instructions Modal */}
        <Modal
            title={null}
            visible={successModalVisible}
            onCancel={() => {
                setSuccessModalVisible(false);
                setFiledDispute(null);
                onSuccess();
            }}
            footer={null}
            centered
            width={700}
            closable={true}
            maskClosable={true}
            className="dispute-success-modal"
            style={{ padding: 0 }}
        >
            <div style={{ 
                background: 'linear-gradient(135deg, #f6ffed 0%, #ffffff 100%)',
                borderRadius: '12px',
                overflow: 'hidden'
            }}>
                {/* Header Section */}
                <div style={{
                    background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                    padding: '20px 24px 16px 24px',
                    textAlign: 'center',
                    borderBottom: 'none'
                }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        margin: '0 auto 12px',
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}>
                        <CheckCircleOutlined style={{ fontSize: '24px', color: 'white' }} />
                    </div>
                    <Title level={4} style={{ 
                        color: 'white', 
                        margin: 0,
                        fontWeight: 600,
                        fontSize: '20px'
                    }}>
                        Dispute Filed Successfully!
                    </Title>
                    {filedDispute?.dispute_number && (
                        <Text style={{ 
                            color: 'rgba(255, 255, 255, 0.9)', 
                            fontSize: '13px',
                            display: 'block',
                            marginTop: '6px'
                        }}>
                            Dispute Number: <strong>{filedDispute.dispute_number}</strong>
                        </Text>
                    )}
                </div>

                {/* Content Section */}
                <div style={{ padding: '24px' }}>
                    <Alert
                        message={<Text strong style={{ fontSize: '13px' }}>What Happens Next?</Text>}
                        description={
                            <div style={{ fontSize: '12px', marginTop: 8 }}>
                                <Text style={{ display: 'block', marginBottom: 8 }}>
                                    Your dispute has been successfully filed and is now under review. Here's what you can expect:
                                </Text>
                                <ul style={{ margin: 0, paddingLeft: 20, lineHeight: '1.8' }}>
                                    <li>You will receive an email confirmation shortly</li>
                                    <li>Our support team will review your dispute within 24-48 hours</li>
                                    <li>You'll be notified via email when there are updates</li>
                                    <li>You can track the status of your dispute at any time</li>
                                </ul>
                            </div>
                        }
                        type="success"
                        showIcon
                        icon={<InfoCircleOutlined />}
                        style={{ 
                            borderRadius: '8px',
                            border: '1px solid #b7eb8f',
                            marginBottom: '20px',
                            background: '#f6ffed'
                        }}
                    />

                    <Card
                        style={{
                            marginBottom: '20px',
                            borderRadius: '8px',
                            border: '1px solid #d9f7be',
                            background: '#f6ffed'
                        }}
                        bodyStyle={{ padding: '16px' }}
                    >
                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <ClockCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                                <Text strong style={{ fontSize: '14px' }}>Response Time</Text>
                            </div>
                            <Text style={{ fontSize: '13px', color: '#595959', marginLeft: 26 }}>
                                Our support team typically responds within 24-48 hours. For urgent matters, we prioritize based on the priority level you selected.
                            </Text>
                        </Space>
                    </Card>

                    <Card
                        style={{
                            marginBottom: '20px',
                            borderRadius: '8px',
                            border: '1px solid #d9f7be',
                            background: '#f6ffed'
                        }}
                        bodyStyle={{ padding: '16px' }}
                    >
                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <MailOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                                <Text strong style={{ fontSize: '14px' }}>Email Notifications</Text>
                            </div>
                            <Text style={{ fontSize: '13px', color: '#595959', marginLeft: 26 }}>
                                You'll receive email updates whenever there are changes to your dispute status or when our team needs additional information.
                            </Text>
                        </Space>
                    </Card>

                    <Card
                        style={{
                            marginBottom: '24px',
                            borderRadius: '8px',
                            border: '1px solid #d9f7be',
                            background: '#f6ffed'
                        }}
                        bodyStyle={{ padding: '16px' }}
                    >
                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <EyeOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                                <Text strong style={{ fontSize: '14px' }}>Track Your Dispute</Text>
                            </div>
                            <Text style={{ fontSize: '13px', color: '#595959', marginLeft: 26 }}>
                                You can view and track the status of your dispute anytime from the Disputes page. Look for your dispute number: <strong>{filedDispute?.dispute_number || 'N/A'}</strong>
                            </Text>
                        </Space>
                    </Card>

                    <Divider style={{ margin: '24px 0', borderColor: '#d9f7be' }} />

                    <div style={{ 
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 12
                    }}>
                        <Button 
                            onClick={() => {
                                setSuccessModalVisible(false);
                                setFiledDispute(null);
                                onSuccess();
                            }}
                            size="large"
                            style={{ 
                                borderRadius: '8px',
                                height: '44px',
                                padding: '0 24px'
                            }}
                        >
                            Close
                        </Button>
                        <Button
                            type="primary"
                            icon={<EyeOutlined />}
                            onClick={() => {
                                setSuccessModalVisible(false);
                                setFiledDispute(null);
                                onSuccess();
                                // Navigate to dispute detail page if dispute number is available
                                if (filedDispute?.dispute_number) {
                                    navigate(`/disputes/${filedDispute.dispute_number.toLowerCase()}`);
                                }
                            }}
                            size="large"
                            style={{
                                borderRadius: '8px',
                                height: '44px',
                                fontWeight: 500,
                                padding: '0 24px',
                                background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                                border: 'none',
                                boxShadow: '0 2px 8px rgba(82, 196, 26, 0.3)'
                            }}
                        >
                            Details
                        </Button>
                    </div>
                </div>
            </div>
            <style>{`
                .dispute-success-modal .ant-modal-content {
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
                }
                
                .dispute-success-modal .ant-modal-close {
                    display: flex;
                    font-size: 16px;
                    font-style: normal;
                    line-height: 32px;
                    justify-content: center;
                    text-transform: none;
                    text-rendering: auto;
                    color: #ffffff;
                    padding: 10px;
                    background: #333333;
                    border-radius: 50%;
                    width: 30px;
                    height: 30px;
                    top: 12px;
                    right: 12px;
                }
                
                .dispute-success-modal .ant-modal-close:hover {
                    color: white;
                }
            `}</style>
        </Modal>
        </>
    );
};

export default FileDisputeModal;

